import { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    Loader2, ClipboardList, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Check, X,
} from 'lucide-react';
import { useClientAttendanceQuery } from '../../services/hooks/useClientes';

interface ClientAttendanceCardProps {
    clientId?: string;
}

/**
 * P6 — a client's attendance.
 *
 * Reads the `attendances` collection, not the activity feed: the feed carries a
 * row per mark so it shows in the timeline, but it expires at 60 days. This does
 * not, which is the whole reason the collection exists.
 *
 * The one thing this screen must not do is imply that present + absent is the
 * client's class count. Classes nobody took the register for are not absences,
 * so the totals are deliberately labelled as "marked" rather than as a balance.
 */
const ClientAttendanceCard = ({ clientId }: ClientAttendanceCardProps) => {
    const [open, setOpen] = useState(false);
    const [page, setPage] = useState(1);
    const { data, isLoading, isFetching } = useClientAttendanceQuery(open ? clientId : undefined, page);

    const items = data?.items ?? [];
    const present = data?.summary.present ?? 0;
    const absent = data?.summary.absent ?? 0;
    const marked = present + absent;
    const pages = data?.pages ?? 1;

    // Share of the classes SOMEBODY MARKED, never of the client's classes.
    const rate = marked > 0 ? Math.round((present / marked) * 100) : null;

    return (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
            <button onClick={() => setOpen((v) => !v)} className="w-full flex justify-between items-center">
                <div className="flex items-center">
                    <ClipboardList className="mr-2" size={20} />
                    <h2 className="text-lg font-bold">Asistencia</h2>
                </div>
                {open ? <ChevronUp /> : <ChevronDown />}
            </button>

            {open && (
                <>
                    {isLoading ? (
                        <div className="flex justify-center my-4"><Loader2 className="animate-spin" /></div>
                    ) : (
                        <>
                            <div className="grid grid-cols-3 gap-2 mt-4">
                                <div className="rounded-lg border p-3 text-center">
                                    <div className="text-xl font-bold text-green-700">{present}</div>
                                    <div className="text-xs text-gray-500">Vino</div>
                                </div>
                                <div className="rounded-lg border p-3 text-center">
                                    <div className="text-xl font-bold text-red-600">{absent}</div>
                                    <div className="text-xs text-gray-500">No vino</div>
                                </div>
                                <div className="rounded-lg border p-3 text-center">
                                    <div className="text-xl font-bold">{rate === null ? '—' : `${rate}%`}</div>
                                    <div className="text-xs text-gray-500">Asistencia</div>
                                </div>
                            </div>

                            {/* Sin esto los números se leen como el total de clases
                                del cliente, y no cuadran nunca: lo que nadie marcó
                                no es una ausencia. */}
                            <p className="text-xs text-gray-400 mt-2">
                                Sobre las {marked} clase{marked !== 1 ? 's' : ''} en las que se pasó lista.
                                Las clases en las que no se pasó no cuentan como falta.
                            </p>

                            {items.length === 0 ? (
                                <p className="text-gray-500 my-4">Todavía no se ha pasado lista en ninguna clase de este cliente.</p>
                            ) : (
                                <ul className="mt-4 space-y-2">
                                    {items.map((row) => (
                                        <li key={row._id} className="flex items-start gap-3 border rounded-lg p-3">
                                            <span
                                                className={`mt-0.5 rounded-full p-1.5 ${
                                                    row.status === 'present'
                                                        ? 'text-green-700 bg-green-50'
                                                        : 'text-red-600 bg-red-50'
                                                }`}
                                            >
                                                {row.status === 'present' ? <Check size={16} /> : <X size={16} />}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-sm">
                                                    {row.status === 'present' ? 'Vino a clase' : 'No vino a clase'}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    {[
                                                        row.levelName,
                                                        row.fecha && format(new Date(row.fecha), 'EEEE d MMM', { locale: es }),
                                                        row.horaInicio,
                                                    ].filter(Boolean).join(' · ') || 'Clase'}
                                                </div>
                                                {/* Una falta le cuesta la clase, así que
                                                    la fila dice quién la marcó. */}
                                                {row.markedBy && (
                                                    <div className="text-xs text-gray-400">{row.markedBy}</div>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {pages > 1 && (
                                <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
                                    <span>
                                        Página {page} de {pages}
                                        {isFetching && <Loader2 className="animate-spin inline ml-2" size={14} />}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            disabled={page <= 1}
                                            className="p-1 rounded border disabled:opacity-40 hover:bg-gray-50"
                                            aria-label="Página anterior"
                                        >
                                            <ChevronLeft size={18} />
                                        </button>
                                        <button
                                            onClick={() => setPage((p) => Math.min(pages, p + 1))}
                                            disabled={page >= pages}
                                            className="p-1 rounded border disabled:opacity-40 hover:bg-gray-50"
                                            aria-label="Página siguiente"
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ClientAttendanceCard;
